import { useCallback, useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import { FilterDropdown } from "../../shared/ui/FilterDropdown"
import {
  listSubscriptionPlansApi,
  createSubscriptionPlanApi,
  updateSubscriptionPlanApi,
  SubscriptionPlan,
  SubscriptionTargetType,
} from "../../features/payments/api/payments.api"
import {
  TagIcon,
  FunnelIcon,
  PlusIcon,
  BuildingOffice2Icon,
  BriefcaseIcon,
  PencilSquareIcon,
} from "@heroicons/react/16/solid"
import { XMarkIcon } from "@heroicons/react/20/solid"

const LIMIT = 50

const TARGET_CONFIG: Record<SubscriptionTargetType, { label: string; icon: React.ReactNode }> = {
  china_access: { label: "Доступ к Китаю",     icon: <BuildingOffice2Icon className="w-3.5 h-3.5" /> },
  job_access:   { label: "Доступ к вакансиям", icon: <BriefcaseIcon className="w-3.5 h-3.5" /> },
}

const TARGET_OPTIONS: SubscriptionTargetType[] = ["china_access", "job_access"]

const emptyForm = {
  codename: "",
  title: "",
  target_type: "china_access" as SubscriptionTargetType,
  amount: 0,
  is_active: true,
}

export const SubscriptionPlansPage = () => {
  const [items, setItems] = useState<SubscriptionPlan[]>([])
  const [loading, setLoading] = useState(true)

  const [filterActive, setFilterActive] = useState<"active" | "inactive" | "">("")
  const [filterTarget, setFilterTarget] = useState<SubscriptionTargetType | "">("")

  const [editing, setEditing] = useState<SubscriptionPlan | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const load = useCallback(async () => {
    setLoading(true)
    const res = await listSubscriptionPlansApi({
      limit: LIMIT,
      is_active: filterActive ? filterActive === "active" : undefined,
      target_type: filterTarget,
    })
    setItems(res.data.items)
    setLoading(false)
  }, [filterActive, filterTarget])

  useEffect(() => { load() }, [load])

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setError("")
    setFormOpen(true)
  }

  const openEdit = (plan: SubscriptionPlan) => {
    setEditing(plan)
    setForm({ codename: plan.codename, title: plan.title, target_type: plan.target_type, amount: plan.amount, is_active: plan.is_active })
    setError("")
    setFormOpen(true)
  }

  const handleSave = async () => {
    if (!form.codename.trim() || !form.title.trim()) {
      setError("Заполните codename и название")
      return
    }
    setSaving(true)
    setError("")
    try {
      if (editing) {
        const res = await updateSubscriptionPlanApi(editing.id, {
          codename: form.codename.trim(),
          title: form.title.trim(),
          target_type: form.target_type,
          amount: form.amount,
          is_active: form.is_active,
        })
        setItems((prev) => prev.map((p) => (p.id === editing.id ? res.data : p)))
      } else {
        const res = await createSubscriptionPlanApi({
          codename: form.codename.trim(),
          title: form.title.trim(),
          target_type: form.target_type,
          amount: form.amount,
          currency: "KGS",
          billing_period: "monthly",
          is_active: form.is_active,
        })
        setItems((prev) => [res.data, ...prev])
      }
      setFormOpen(false)
    } catch {
      setError("Не удалось сохранить тариф")
    } finally {
      setSaving(false)
    }
  }

  return (
    <DashboardLayout title="Тарифы">
      <div className="bg-white rounded-xl border overflow-hidden">

        {/* ── Filters ── */}
        <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-b bg-gray-50/60">
          <FunnelIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />

          <FilterDropdown
            label="Тип"
            value={filterTarget}
            onChange={setFilterTarget}
            options={TARGET_OPTIONS.map((t) => ({ value: t, label: TARGET_CONFIG[t].label }))}
          />

          <FilterDropdown
            label="Статус"
            value={filterActive}
            onChange={setFilterActive}
            options={[
              { value: "active", label: "Активные", dot: "bg-emerald-400" },
              { value: "inactive", label: "Скрытые", dot: "bg-gray-400" },
            ]}
          />

          {(filterActive || filterTarget) && (
            <button
              type="button"
              onClick={() => { setFilterActive(""); setFilterTarget("") }}
              className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
            >
              Сбросить фильтры
            </button>
          )}

          <button
            onClick={openCreate}
            className="ml-auto inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
          >
            <PlusIcon className="w-3.5 h-3.5" />
            Новый тариф
          </button>
        </div>

        {/* ── Table ── */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50/40">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Тариф</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Codename</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Тип доступа</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Цена</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Статус</th>
                <th className="px-5 py-3 w-16" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {[1, 2, 3, 4, 5, 6].map((j) => (
                      <td key={j} className="px-5 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse w-24" /></td>
                    ))}
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-16 text-center">
                    <TagIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">Тарифов пока нет</p>
                  </td>
                </tr>
              ) : (
                items.map((plan) => (
                  <tr key={plan.id} onClick={() => openEdit(plan)} className="hover:bg-gray-50/60 transition-colors cursor-pointer group">
                    <td className="px-5 py-3.5 font-medium text-gray-800">{plan.title}</td>
                    <td className="px-5 py-3.5"><span className="font-mono text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md">{plan.codename}</span></td>
                    <td className="px-5 py-3.5">
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                        {TARGET_CONFIG[plan.target_type]?.icon}
                        {TARGET_CONFIG[plan.target_type]?.label ?? plan.target_type}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-700">{plan.amount.toLocaleString("ru-RU")} {plan.currency} / мес</td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${plan.is_active ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : "bg-gray-100 text-gray-500 ring-1 ring-gray-200"}`}>
                        {plan.is_active ? "Активен" : "Скрыт"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5">
                      <button
                        onClick={(e) => { e.stopPropagation(); openEdit(plan) }}
                        className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-blue-600 transition-colors px-2 py-1.5 rounded-lg hover:bg-blue-50 opacity-0 group-hover:opacity-100"
                      >
                        <PencilSquareIcon className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Create/Edit drawer ── */}
      {createPortal(
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity duration-300 ${formOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={() => setFormOpen(false)}
          />
          <div className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${formOpen ? "translate-x-0" : "translate-x-full"}`}>
            {/* Header */}
            <div className={`bg-gradient-to-r ${editing ? "from-amber-500 to-yellow-400" : "from-blue-600 to-blue-500"} px-6 pt-6 pb-8 flex-shrink-0 relative`}>
              <button onClick={() => setFormOpen(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                <XMarkIcon className="w-4 h-4 text-white" />
              </button>

              <div className="flex items-center gap-2 mb-3">
                <TagIcon className="w-5 h-5 text-white/80" />
                <span className="text-white/80 text-sm font-medium uppercase tracking-wider">
                  {editing ? "Редактировать" : "Новый тариф"}
                </span>
              </div>

              <p className="text-white font-semibold text-lg pr-10">
                {editing ? editing.title : "Создание тарифа"}
              </p>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Codename</label>
                <input
                  value={form.codename}
                  onChange={(e) => setForm((f) => ({ ...f, codename: e.target.value }))}
                  placeholder="china_access_monthly"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Название</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Доступ к Китаю — месяц"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Тип доступа</label>
                <select
                  value={form.target_type}
                  onChange={(e) => setForm((f) => ({ ...f, target_type: e.target.value as SubscriptionTargetType }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {TARGET_OPTIONS.map((t) => (
                    <option key={t} value={t}>{TARGET_CONFIG[t].label} ({t})</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-400 mt-1">Программный ключ — бэкенд решает по нему, что открывать. Новые значения заводит разработчик.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Цена</label>
                  <input
                    type="number"
                    value={form.amount}
                    onChange={(e) => setForm((f) => ({ ...f, amount: Number(e.target.value) }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Валюта / период</label>
                  <div className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500">KGS / ежемесячно</div>
                </div>
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Тариф активен (доступен для продажи)</span>
              </label>

              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50/80 flex gap-2">
              <button
                onClick={() => setFormOpen(false)}
                className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-white transition-colors"
              >
                Отмена
              </button>
              <button
                disabled={saving}
                onClick={handleSave}
                className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white py-2.5 rounded-xl text-sm font-medium transition-colors"
              >
                {saving && <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                Сохранить
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
    </DashboardLayout>
  )
}